import { reactive, nextTick } from 'vue';
import { defineStore } from 'pinia';
import { ImportFileType } from '@/coreDomainsModule/entities/unmanagedBusiness';
import { TableContentItem } from '@/corePagesModule/BaseTableContent/interface/tableContentItems';
import { contentPropertyType } from '../../constants/contentProperty';
import { BusinessFactory } from '@/coreDomainsModule/factories/BusinessFactory';
import { toast } from '../../../corePagesModule/components/Toast/ToastManager';
import { FilterType } from '@/coreDomainsModule/entities/filter';
import { TableType } from '@/coreDomainsModule/table';
import { keyMappingForSelectInput, selectOption } from '../../constants/formKeyMap';
import { UnmanagedBusinessState, ModalInfo } from './types';
import { modalStateDefault, unmanagedBusinessStateDefault } from './unmanagedBusinessStateDefault';
import { UnmanagedBusinessService } from '@/unmanagedBusinessModule/domains/services/UnmanagedBusinessService';
import { DateService } from '@/coreDomainsModule/services/dateService';
import { simpleCopy } from '@/utils/deepCopy';
import { assertNever } from '@/coreDomainsModule/assertNever';
import { i18n } from '@/i18n';
import { FilterContentItem } from '@/corePagesModule/filterModal/filterModal';
import { diContainer } from '@/coreContainer/container';
import { TableContentProperty } from '@/corePagesModule/BaseTableContent/interface/tableContentHeaders';
import { initPagingOptions } from '@/coreDomainsModule/paging';
import { useTableFilter } from '@/corePagesModule/composables/useTableFilter';

export const useUnmanagedBusinessStore = defineStore('unmanagedBusinessStore', () => {
  let isWaitOpenModal = false;

  const state = reactive<UnmanagedBusinessState>(simpleCopy(unmanagedBusinessStateDefault));
  const t = i18n.t;

  /**
   *  テーブルタイプを取得する
   * @param fileType - 取引の種類
   * @returns - 取引の種類に応じたテーブルタイプ
   */
  const getTableType = (fileType: ImportFileType): TableType => {
    switch (fileType) {
      case 'announce':
        return 'unmanagedBusinessTypeAnnounce';
      case 'arrival':
        return 'unmanagedBusinessTypeArrival';
      case 'inventory':
        return 'unmanagedBusinessTypeInventory';
      case 'salesResult':
        return 'unmanagedBusinessTypeSalesResult';
      default:
        return assertNever(fileType);
    }
  };

  // フィルター情報のComposable
  const summaryFilterComposable: { [key in ImportFileType]: ReturnType<typeof useTableFilter> } = {
    announce: useTableFilter(getTableType('announce')),
    arrival: useTableFilter(getTableType('arrival')),
    inventory: useTableFilter(getTableType('inventory')),
    salesResult: useTableFilter(getTableType('salesResult')),
  };

  // フィルタ情報getだけ抜き出したObject
  const summaryGetFilterItemValue = Object.fromEntries(
    Object.entries(summaryFilterComposable).map(([key, value]) => [key, value.getFilterItemsValue])
  );

  /**
   * 未管理の取引を取得する
   */
  const fetchUnmanagedBusinesses = async () => {
    await summaryFilterComposable[state.selectedFileType].initTableFilter();
    state.tableInfo[state.selectedFileType].loadingState = {
      status: { type: 'loading' },
    };
    const res = diContainer.getAllUnmanagedBusinessUseCase.execute(
      state.tableInfo[state.selectedFileType].pagingState,
      state.selectedFileType,
      summaryFilterComposable[state.selectedFileType].cachedFilterItems.value,
      state.tableInfo[state.selectedFileType].sortState.isAsc,
      state.tableInfo[state.selectedFileType].sortState.sortTarget
    );
    switch (res.status.type) {
      case 'success':
        state.businesses = res.status.data[0];
        setTableInfo(state.selectedFileType, true);
        break;
      case 'error':
        state.tableInfo[state.selectedFileType].loadingState = {
          status: {
            type: 'error',
            errorMessage: res.status.error.message,
          },
        };
        break;
    }
  };

  /**
   * テーブルに表示するファイル種類を変更する
   * @param fileType - 取引の種類
   */
  const changeSelectedFileType = (fileType: ImportFileType) => {
    state.selectedFileType = fileType;
  };

  /**
   * テーブルの情報を設定する
   * @param fileType - 取引の種類
   * @param isForce - 強制的に設定するか
   */
  const setTableInfo = (fileType: ImportFileType, isForce = false) => {
    // 既に選択されているファイルタイプと同じ場合は何もしない
    if (fileType === state.selectedFileType && isForce === false) return;

    state.tableInfo[state.selectedFileType].loadingState = {
      status: { type: 'loading' },
    };

    state.selectedFileType = fileType;
    state.tableInfo[fileType].tableContentProperties = contentPropertyType[fileType];
    state.tableInfo[fileType].tableContentItems = state.businesses.map((business) => {
      return {
        displayId: business.businessId,
        isVirtualChild: false,
        ...business,
      };
    });
    state.tableInfo[fileType].selectOptionInfo = {};
    selectOption[fileType].forEach((option) => {
      state.tableInfo[fileType].selectOptionInfo[option] = [];
    });
    // データが0件の場合はloadingStateをzeroMatchに変更
    if (state.tableInfo[fileType].tableContentItems.length === 0) {
      state.tableInfo[fileType].loadingState = {
        status: { type: 'zeroMatch' },
      };
    } else {
      // 3秒まつ
      state.tableInfo[fileType].loadingState = {
        status: { type: 'success' },
      };
    }
  };

  /**
   * 選択された取引を設定する
   * @param item - 選択された取引
   */
  const setSelectedBusiness = (item: TableContentItem) => {
    // モーダルのStateを初期化
    state.modalInfo.modalState = simpleCopy(modalStateDefault);
    // 未管理情報を初期化
    state.settingUnmanagedBusiness = simpleCopy(unmanagedBusinessStateDefault.settingUnmanagedBusiness);

    state.settingUnmanagedBusiness.businessId = item.businessId;
    const keys = [
      'supplierName',
      'supplierNumber',
      'businessNumber',
      'businessName',
      'package',
      'warehouse',
      'warehouseNumber',
      'customerName',
      'customerNumber',
      'salesDestinationName',
      'salesDestinationNumber',
      'unit',
      'leadTime',
      'leadTimeUnit',
      'moq',
      'endUser',
      'manufacturer',
    ];

    keys.forEach((key) => {
      if (item[key]) state.modalInfo.modalState.registerBusiness[key] = item[key];
    });

    if (item.stdInventoryIndices && item.stdInventoryIndices['標準在庫指数']) {
      const stdIndex = item.stdInventoryIndices['標準在庫指数'];
      if (stdIndex.min) state.modalInfo.modalState.registerBusiness.stdInventoryIndicesMin = stdIndex.min;
      if (stdIndex.max) state.modalInfo.modalState.registerBusiness.stdInventoryIndicesMax = stdIndex.max;
      if (stdIndex.option && stdIndex.option.unit)
        state.modalInfo.modalState.registerBusiness.stdInventoryIndicesUnit = stdIndex.option.unit;
    }

    // 内示・入荷・在庫・売上の情報を取得
    switch (state.selectedFileType) {
      case ImportFileType.Announce:
        state.settingUnmanagedBusiness.announce = item.announce;
        break;
      case ImportFileType.SalesResult:
        state.settingUnmanagedBusiness.salesResults = item.salesResults;
        break;
      case ImportFileType.Inventory:
        state.settingUnmanagedBusiness.inventories = item.inventories;
        break;
      case ImportFileType.Arrival:
        state.settingUnmanagedBusiness.arrivals = item.arrivals;
        break;
      default:
        return assertNever(state.selectedFileType);
    }
  };

  /**
   * modalを開く
   * @param modalName - 開くmodalの名前
   */
  const openModal = async (modalName: keyof ModalInfo['isOpen']) => {
    // エラーモーダルが開いている間は無視する
    if (state.modalInfo.isOpen.error) return;
    // モーダル開始処理が重複することがあるので、開始処理中は無視する
    if (isWaitOpenModal) return;
    isWaitOpenModal = true;

    // 全てのmodalを閉じる
    Object.keys(state.modalInfo.isOpen).forEach((key) => {
      state.modalInfo.isOpen[key as keyof ModalInfo['isOpen']] = false;
    });

    // 指定のモーダルを開く
    state.modalInfo.isOpen[modalName] = true;
    nextTick(() => {
      isWaitOpenModal = false;
    });
  };

  /**
   * エラーモーダルを開く
   * エラーモーダルは他のモーダルよりも優先度が高い
   * @param errorTextForConsole - コンソールに表示するエラーテキスト
   */
  const openErrorModal = (errorTextForConsole?: string) => {
    // 全てのmodalを閉じる
    Object.keys(state.modalInfo.isOpen).forEach((key) => {
      state.modalInfo.isOpen[key as keyof ModalInfo['isOpen']] = false;
    });

    state.modalInfo.isOpen.error = true;

    if (errorTextForConsole) console.error(errorTextForConsole);
  };

  /**
   * 未管理の取引を登録する
   * @param isExistAggregateBusiness - 集計取引が存在するか
   */
  const registerBusiness = async (isExistAggregateBusiness?: boolean) => {
    // loading
    state.modalInfo.isOpen.loading = true;

    const registerNetworkResult = await diContainer.registerBusinessFromUnmanagedBusinessUseCase.execute(
      state.settingUnmanagedBusiness,
      state.selectedFileType,
      isExistAggregateBusiness
    );

    if (registerNetworkResult.status.type === 'error') return openErrorModal('取引の登録に失敗しました');

    const deleteNetworkResult = diContainer.deleteUnmanagedBusinessUseCase.execute(state.settingUnmanagedBusiness.businessId);

    if (deleteNetworkResult.status.type === 'error') return openErrorModal('未管理の取引の削除に失敗しました（登録はすでに完了済み）');

    state.modalInfo.isOpen.loading = false;

    // テーブルから削除
    state.businesses = state.businesses.filter((business) => business.businessId !== state.settingUnmanagedBusiness.businessId);
    setTableInfo(state.selectedFileType, true);

    openModal('successRegister');
  };

  /**
   * 前のモーダル情報を参考に次のモーダルを開く
   * @param previousModalName - 前のモーダルの名前
   */
  const openModalByPreviousModal = async (previousModalName: keyof ModalInfo['isOpen']) => {
    switch (previousModalName) {
      case 'registerBusiness':
        // モーダルに入力された情報をセットする
        Object.assign(state.settingUnmanagedBusiness, {
          supplierName: state.modalInfo.modalState.registerBusiness.supplierName,
          supplierNumber: state.modalInfo.modalState.registerBusiness.supplierNumber,
          businessNumber: state.modalInfo.modalState.registerBusiness.businessNumber,
          businessName: state.modalInfo.modalState.registerBusiness.businessName,
          package: state.modalInfo.modalState.registerBusiness.package,
          warehouse: state.modalInfo.modalState.registerBusiness.warehouseName,
          warehouseNumber: state.modalInfo.modalState.registerBusiness.warehouseNumber,
          customerName: state.modalInfo.modalState.registerBusiness.customerName,
          customerNumber: state.modalInfo.modalState.registerBusiness.customerNumber,
          salesDestinationName: state.modalInfo.modalState.registerBusiness.salesDestinationName,
          salesDestinationNumber: state.modalInfo.modalState.registerBusiness.salesDestinationNumber,
          unit: state.modalInfo.modalState.registerBusiness.unit,
          leadTime: Number(state.modalInfo.modalState.registerBusiness.leadTime),
          leadTimeUnit: state.modalInfo.modalState.registerBusiness.leadTimeUnit,
          stdInventoryIndices: {
            標準在庫指数: {
              min: Number(state.modalInfo.modalState.registerBusiness.stdInventoryIndicesMin),
              max: Number(state.modalInfo.modalState.registerBusiness.stdInventoryIndicesMax),
              option: { unit: state.modalInfo.modalState.registerBusiness.stdInventoryIndicesUnit },
            },
          },
          moq: Number(state.modalInfo.modalState.registerBusiness.moq),
          endUser: state.modalInfo.modalState.registerBusiness.endUser,
          manufacturer: state.modalInfo.modalState.registerBusiness.manufacturer,
          businessFreeColumnValues: state.modalInfo.modalState.registerBusiness.businessFreeColumnValues.map((item) => ({ ...item })),
        });

        let isDetectDuplication = false;

        switch (state.selectedFileType) {
          case ImportFileType.Announce:
          case ImportFileType.SalesResult:
            /**
             * 1. 関連取引で重複が存在するか? と 集計取引が存在するか? を確認
             * 2. 関連取引が重複するか? でモーダルが分岐する
             * 3-1. 重複しない場合、集計取引が存在するか?の結果を保存しておき、重複がない場合のモーダルを開く
             * 3-2. 重複する場合、重複がある場合のモーダルを開く
             */
            state.modalInfo.isOpen.loading = true;
            const networkResult = await diContainer.checkDuplicateBaseBusinessUseCase.execute(state.settingUnmanagedBusiness);
            state.modalInfo.isOpen.loading = false;

            if (networkResult.status.type === 'error') return openErrorModal();

            // 関連取引が存在するか = 重複しているか
            isDetectDuplication = !!networkResult.status.data.baseBusinessId;

            // 集計取引が存在するか
            state.modalInfo.modalState.noDuplication.existsAggregateBusiness = !!networkResult.status.data.aggregateBusinessId;

            // API保存時に使用するため、集計取引のIDをメモ
            state.settingUnmanagedBusiness.aggregateBusinessId = networkResult.status.data.aggregateBusinessId;
            state.settingUnmanagedBusiness.baseBusinessId = networkResult.status.data.baseBusinessId;

            // noDuplicateの場合はモーダルのテーブルに表示する取引をセット
            if (isDetectDuplication) break;
            state.tableInfo.modalWhenAggregateRow.tableContentItems = [];
            // 現在の取引情報を追加
            state.tableInfo.modalWhenAggregateRow.tableContentItems.push({
              displayId: state.settingUnmanagedBusiness.businessId,
              isVirtualChild: false,
              ...BusinessFactory.create({}),
              ...state.settingUnmanagedBusiness,
            });
            // 他の関連取引情報を追加
            for (const business of networkResult.status.data.existsBusinessData) {
              const packageAmount = business.package.toString();
              state.tableInfo.modalWhenAggregateRow.tableContentItems.push({
                displayId: business.baseBusinessId,
                isVirtualChild: true,
                ...BusinessFactory.create({ ...business, package: packageAmount, businessId: business.baseBusinessId }),
              });
            }

            break;
          case ImportFileType.Inventory:
          case ImportFileType.Arrival:
            /**
             * 1. 集計取引が存在するか? を確認
             * 2. 結果でモーダルが分岐する
             * 3-1. 集計取引が存在しない場合、重複がない場合のモーダルを開く
             * 3-2. 集計取引が存在する場合、単位・リードタイム・標準在庫指数・MOQに差分があるか?の結果を保存しておき、差分がない場合のモーダルを開く
             */
            state.modalInfo.isOpen.loading = true;
            const networkResultForInventory = await diContainer.checkDuplicateAggregateBusinessUseCase.execute(
              state.settingUnmanagedBusiness
            );
            state.modalInfo.isOpen.loading = false;

            if (networkResultForInventory.status.type === 'error') return openErrorModal();

            // 集計取引が存在するか = 重複しているか
            isDetectDuplication = !!networkResultForInventory.status.data.aggregateBusinessId;
            state.settingUnmanagedBusiness.aggregateBusinessId = networkResultForInventory.status.data.aggregateBusinessId ?? '';

            state.modalInfo.modalState.checkDuplication.isDiff =
              networkResultForInventory.status.data.hasDifferenceAggregateBusinessSubInfo;
            if (
              networkResultForInventory.status.data.hasDifferenceAggregateBusinessSubInfo === true &&
              networkResultForInventory.status.data.differenceAggregateBusinessSubInfo === undefined
            )
              // 差分があるのに差分情報が取得できなかった場合はエラーを表示（バックエンド側の不具合と思われる）
              return openErrorModal('集計取引の差分情報が取得できませんでした');
            if (networkResultForInventory.status.data.differenceAggregateBusinessSubInfo) {
              state.modalInfo.modalState.checkDuplication.diffInfo =
                networkResultForInventory.status.data.differenceAggregateBusinessSubInfo;
              // リードタイムは計算する
              switch (state.modalInfo.modalState.checkDuplication.diffInfo.leadTimeUnit) {
                case 'month':
                  state.modalInfo.modalState.checkDuplication.diffInfo.leadTime /= 30;
                  break;
                case 'week':
                  state.modalInfo.modalState.checkDuplication.diffInfo.leadTime /= 7;
                  break;
              }
            }
            break;
          default:
            return assertNever(state.selectedFileType);
        }

        if (isDetectDuplication) {
          openModal('detectDuplication');
        } else {
          openModal('noDuplication');
        }
        break;

      case 'noDuplication':
        switch (state.selectedFileType) {
          case ImportFileType.Announce:
          case ImportFileType.SalesResult:
            if (state.modalInfo.modalState.noDuplication.existsAggregateBusiness) {
              // 取引を登録した集計行の関連取引を全て表示するモーダルを開く
              openModal('successRegisterWithAggregate');
            } else {
              state.modalInfo.modalState.registerLeadTimeAndEtc.warehouseName = state.settingUnmanagedBusiness.warehouse;
              state.modalInfo.modalState.registerLeadTimeAndEtc.supplierName = state.settingUnmanagedBusiness.supplierName;
              state.modalInfo.modalState.registerLeadTimeAndEtc.businessName = state.settingUnmanagedBusiness.businessName ?? '';
              state.modalInfo.modalState.registerLeadTimeAndEtc.warehouseNumber = state.settingUnmanagedBusiness.warehouseNumber;
              state.modalInfo.modalState.registerLeadTimeAndEtc.supplierNumber = state.settingUnmanagedBusiness.supplierNumber;
              state.modalInfo.modalState.registerLeadTimeAndEtc.businessNumber = state.settingUnmanagedBusiness.businessNumber;
              state.modalInfo.modalState.registerLeadTimeAndEtc.package = state.settingUnmanagedBusiness.package;
              openModal('questionUpdateExistBusinessLeadTimeAndStdInventoryIndices');
            }
            break;
          case ImportFileType.Inventory:
            openModal('registerInventoryExecutionDateModal');
            break;
          case ImportFileType.Arrival:
            // 入荷の場合はそのまま登録
            await registerBusiness();
            break;
          default:
            return assertNever(state.selectedFileType);
        }
        break;

      case 'detectDuplication':
        switch (state.selectedFileType) {
          case ImportFileType.Inventory:
          case ImportFileType.Arrival:
            // リードタイム等に差分があるかチェックする
            if (state.modalInfo.modalState.checkDuplication.isDiff) {
              // 差分がある場合は差分更新を操作するモーダルを開く
              // モーダル表示前にテーブルに情報をセット
              state.tableInfo.modal.tableContentItems = [];
              state.tableInfo.modal.tableContentItems.push({
                displayId: state.settingUnmanagedBusiness.businessId,
                isVirtualChild: false,
                ...BusinessFactory.create({}),
                ...state.settingUnmanagedBusiness,
              });
              openModal('questionUpdateExistBusinessDiff');
              return;
            }
            break;

          case ImportFileType.Announce:
          case ImportFileType.SalesResult:
            break;

          default:
            return assertNever(state.selectedFileType);
        }

        // モーダル表示前にテーブルに情報をセット
        if (UnmanagedBusinessService.isAggregateRow(state.selectedFileType)) {
          state.tableInfo.modalWhenAggregateRow.tableContentItems = [];
          state.tableInfo.modalWhenAggregateRow.tableContentItems.push({
            displayId: state.settingUnmanagedBusiness.businessId,
            isVirtualChild: false,
            ...BusinessFactory.create({}),
            ...state.settingUnmanagedBusiness,
          });
        } else {
          state.tableInfo.modal.tableContentItems = [];
          state.tableInfo.modal.tableContentItems.push({
            displayId: state.settingUnmanagedBusiness.businessId,
            isVirtualChild: false,
            ...BusinessFactory.create({}),
            ...state.settingUnmanagedBusiness,
          });
        }

        // 重複がある場合は上書きするか確認するモーダルを開く
        openModal('questionUpdateExistBusiness');
        break;

      // 差分更新の確認モーダルからの遷移
      case 'questionUpdateExistBusinessDiff':
        state.tableInfo.modal.tableContentItems = [];
        state.tableInfo.modal.tableContentItems.push({
          displayId: state.settingUnmanagedBusiness.businessId,
          isVirtualChild: false,
          ...BusinessFactory.create({}),
          ...state.settingUnmanagedBusiness,
        });
        openModal('questionUpdateExistBusiness');
        break;

      // 上書きするか確認するモーダルからの遷移
      case 'questionUpdateExistBusiness':
        switch (state.selectedFileType) {
          case ImportFileType.Announce:
            openModal('registerAnnounceStartDateModal');
            break;
          case ImportFileType.Inventory:
            openModal('registerInventoryExecutionDateModal');
            break;
          case ImportFileType.SalesResult:
          case ImportFileType.Arrival:
            await registerBusiness();
            break;

          default:
            return assertNever(state.selectedFileType);
        }
        break;

      case 'questionUpdateExistBusinessLeadTimeAndStdInventoryIndices':
        switch (state.selectedFileType) {
          case ImportFileType.Announce:
            openModal('registerAnnounceStartDateModal');
            break;
          case ImportFileType.SalesResult:
            await registerBusiness();
            break;

          case ImportFileType.Inventory:
          case ImportFileType.Arrival:
            openErrorModal('不正なモーダル遷移が発生しました');
            break;

          default:
            return assertNever(state.selectedFileType);
        }
        break;

      case 'successRegisterWithAggregate':
        switch (state.selectedFileType) {
          case ImportFileType.Announce:
            openModal('registerAnnounceStartDateModal');
            break;
          case ImportFileType.SalesResult:
            await registerBusiness();
            break;

          case ImportFileType.Inventory:
          case ImportFileType.Arrival:
            openErrorModal('不正なモーダル遷移が発生しました');
            break;

          default:
            return assertNever(state.selectedFileType);
        }
        break;

      case 'registerAnnounceStartDateModal':
        // 値を指定した年月よりも新しい内示のみにフィルターする
        state.settingUnmanagedBusiness.announce = state.settingUnmanagedBusiness.announce?.filter(
          (announce) =>
            DateService.compareYearMonths(announce, {
              year: Number(state.modalInfo.modalState.registerAnnounceStartDate.year),
              month: Number(state.modalInfo.modalState.registerAnnounceStartDate.month),
            }) !== -1
        );
        await registerBusiness();
        break;
      case 'registerInventoryExecutionDateModal':
        // 在庫の年月日を登録する
        state.settingUnmanagedBusiness.executionDate = DateService.convertDateToIDate(
          state.modalInfo.modalState.inventoryExecutionDate.executionDate
        );
        await registerBusiness();
        break;

      // 未実装のモーダル遷移
      case 'successRegister':
      case 'successUpdateExistBusiness':
      case 'loading':
      case 'error':
      case 'delete':
        openErrorModal(`未実装のモーダル遷移が発生しました: ${previousModalName}からの遷移`);
        break;

      default:
        return assertNever(previousModalName);
    }
  };

  /**
   * modalを閉じる
   * @param modalName - 閉じるmodalの名前
   */
  const closeModal = (modalName: keyof ModalInfo['isOpen']) => {
    if (isWaitOpenModal) return;
    state.modalInfo.isOpen[modalName] = false;
  };

  /**
   * 選択された取引を削除する
   */
  const deleteCheckedUnmanagedBusiness = () => {
    state.checkedIds[state.selectedFileType].forEach((id) => {
      diContainer.deleteUnmanagedBusinessUseCase.execute(id);
    });
    // Tableデータからも削除する
    state.businesses = state.businesses.filter((business) => !state.checkedIds[state.selectedFileType].includes(business.businessId));
    setTableInfo(state.selectedFileType, true);
    closeModal('delete');
    resetTable();
    toast.success(t('選択した取引を一括削除しました。'));
  };

  /**
   * テーブルをリセットする
   */
  const resetTable = () => {
    state.tableInfo[state.selectedFileType].isReset = true;
    nextTick(() => {
      state.tableInfo[state.selectedFileType].isReset = false;
    });
  };

  /**
   * SelectionOption名をFilterTypeの値に変換する
   * @param secondListKey - 二つ目のリストのキー
   * @returns 一つ目のリストの対応するキー、対応がない場合は undefined
   */
  const convertKey = (secondListKey: string): FilterType => {
    return keyMappingForSelectInput[secondListKey] || '';
  };

  /**
   * 新規追加可能な項目のリストを取得する関数
   * @returns - 新規追加可能な選択肢として表示するリスト
   */
  const getAddableOptions = () => {
    return [...selectOption[state.selectedFileType]].filter((option) => option !== 'unit').map((option: string) => t(option));
  };

  /**
   * フィルターモーダル適用ボタンクリック時の処理
   * @param filterItems - フィルターモーダル内で選択された項目
   */
  const onAppliedFilterModal = async (filterItems: FilterContentItem[]) => {
    await summaryFilterComposable[state.selectedFileType].bulkUpdateFilterInfos(simpleCopy(filterItems));
    await fetchUnmanagedBusinesses();
  };

  /**
   * テーブルのデータをソートする
   * @param sortKey - ソートするヘッダー
   * @param isAsc - 昇順かどうか
   */
  const sortTableContentItems = async (sortKey: TableContentProperty, isAsc: boolean) => {
    state.tableInfo[state.selectedFileType].sortState.isAsc = isAsc;
    state.tableInfo[state.selectedFileType].sortState.sortTarget = sortKey.key;
    await fetchUnmanagedBusinesses();
  };

  return {
    state,
    fetchUnmanagedBusinesses,
    setTableInfo,
    setSelectedBusiness,
    openModal,
    closeModal,
    openModalByPreviousModal,
    deleteCheckedUnmanagedBusiness,
    registerBusiness,
    convertKey,
    getTableType,
    onAppliedFilterModal,
    changeSelectedFileType,
    getAddableOptions,
    sortTableContentItems,
    summaryGetFilterItemValue,
  };
});
